import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CheckCircle2, ChevronRight, Phone, PhoneOff, Voicemail, X } from 'lucide-react';
import { crmApi, type ApiRecord } from '../../lib/api';
import { usePhone } from '../phone/PhoneProvider';

type Disposition = {
	value: string;
	label: string;
	icon: typeof CheckCircle2;
	leadStatus?: string;
	tone: string;
};

// Disposition presets — each maps to a downstream contact lead_status so the
// rep doesn't have to think about pipeline mechanics while cold-calling.
const DISPOSITIONS: Disposition[] = [
	{ value: 'connected_interested', label: 'Connected — interested', icon: CheckCircle2, leadStatus: 'interested', tone: 'border-emerald-400/30 bg-emerald-500/10 text-emerald-200' },
	{ value: 'connected_not_now', label: 'Connected — not now', icon: CheckCircle2, leadStatus: 'future', tone: 'border-indigo-400/30 bg-indigo-500/10 text-indigo-200' },
	{ value: 'voicemail', label: 'Left voicemail', icon: Voicemail, leadStatus: 'attempted', tone: 'border-violet-400/30 bg-violet-500/10 text-violet-200' },
	{ value: 'no_answer', label: 'No answer', icon: PhoneOff, leadStatus: 'attempted', tone: 'border-amber-400/30 bg-amber-500/10 text-amber-200' },
	{ value: 'wrong_number', label: 'Wrong number', icon: X, tone: 'border-danger/30 bg-danger/10 text-danger' },
	{ value: 'do_not_call', label: 'Do not call', icon: X, leadStatus: 'not_interested', tone: 'border-danger/30 bg-danger/10 text-danger' }
];

const STATUS_FILTERS = ['new', 'attempted', 'contacted', 'interested', 'follow_up'] as const;

function fullName(c: ApiRecord) {
	const n = `${String(c.first_name ?? '').trim()} ${String(c.last_name ?? '').trim()}`.trim();
	return n || String(c.email ?? `#${c.id}`);
}

export function CallListPage() {
	const queryClient = useQueryClient();
	const { dial, activeCall, pbxEnabled } = usePhone();

	const [statusFilter, setStatusFilter] = useState<string>('new');
	const [activeId, setActiveId] = useState<number | null>(null);
	const [notes, setNotes] = useState('');
	const [followUpDate, setFollowUpDate] = useState('');

	const contactsQuery = useQuery({
		queryKey: ['call-list', statusFilter],
		queryFn: () => crmApi.contacts({ lead_status: statusFilter, limit: '100' })
	});
	const all = contactsQuery.data ?? [];
	const callable = useMemo(() => all.filter((c) => c.phone && String(c.phone).trim()), [all]);

	// Auto-select the first callable contact when the list (re)loads.
	useEffect(() => {
		if (activeId == null && callable.length > 0) {
			setActiveId(Number(callable[0].id));
		}
		if (activeId != null && !callable.some((c) => Number(c.id) === activeId)) {
			setActiveId(callable[0] ? Number(callable[0].id) : null);
		}
	}, [callable, activeId]);

	const active = callable.find((c) => Number(c.id) === activeId) ?? null;

	const logMutation = useMutation({
		mutationFn: (d: Disposition) =>
			crmApi.logCall({
				contact_id: Number(active!.id),
				to_number: String(active!.phone ?? ''),
				disposition: d.value,
				notes: notes || null,
				lead_status: d.leadStatus ?? null,
				follow_up_date: followUpDate ? new Date(followUpDate).toISOString() : null,
				follow_up_note: notes || null
			}),
		onSuccess: (_data, d) => {
			toast.success(`Logged: ${d.label}`);
			setNotes('');
			setFollowUpDate('');
			// Advance to the next contact.
			const idx = callable.findIndex((c) => Number(c.id) === activeId);
			const next = callable[idx + 1];
			setActiveId(next ? Number(next.id) : null);
			void queryClient.invalidateQueries({ queryKey: ['call-list'] });
			void queryClient.invalidateQueries({ queryKey: ['leads'] });
		},
		onError: (e) => toast.error(e instanceof Error ? e.message : 'Log failed')
	});

	const handleDial = async () => {
		if (!active) return;
		try {
			await dial(String(active.phone ?? ''), Number(active.id), (active.company as ApiRecord | null)?.id as number | undefined, fullName(active));
		} catch (e) {
			toast.error(e instanceof Error ? e.message : 'Dial failed');
		}
	};

	const advance = () => {
		const idx = callable.findIndex((c) => Number(c.id) === activeId);
		const next = callable[idx + 1];
		setActiveId(next ? Number(next.id) : null);
	};

	return (
		<div className="space-y-3">
			<header className="rounded-2xl border border-subtle bg-surface-2 px-4 py-3 shadow-panel">
				<p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-accent">Call list</p>
				<h1 className="mt-1 text-xl font-semibold text-primary">One lead at a time. Dial, disposition, advance.</h1>
				<p className="mt-0.5 text-xs text-muted">
					Pick a status to work, hit dial, log the outcome — the contact's lead status auto-advances and we jump to the next.
					{!pbxEnabled ? ' PBX is not enabled — use the disposition buttons to log without dialing.' : ''}
				</p>
				<div className="mt-3 flex flex-wrap gap-1.5">
					{STATUS_FILTERS.map((s) => (
						<button
							key={s}
							onClick={() => setStatusFilter(s)}
							className={`rounded-xl px-3 py-1.5 text-xs transition ${
								statusFilter === s ? 'bg-accent text-accent-contrast shadow-panel' : 'border border-subtle bg-surface-2 text-secondary hover:bg-surface-hover'
							}`}
						>
							{s.replace('_', ' ')}
						</button>
					))}
				</div>
			</header>

			<div className="grid gap-3 xl:grid-cols-[0.9fr_1.4fr]">
				<aside className="rounded-2xl border border-subtle bg-surface-2 p-3 shadow-panel">
					<div className="flex items-center justify-between">
						<h2 className="text-sm font-semibold text-primary">Queue ({callable.length})</h2>
						{all.length !== callable.length ? (
							<p className="text-[11px] text-muted">{all.length - callable.length} have no phone</p>
						) : null}
					</div>
					<ul className="mt-2 max-h-[60vh] space-y-1 overflow-y-auto">
						{callable.length === 0 ? (
							<li className="rounded-xl border border-dashed border-subtle px-3 py-3 text-xs text-muted">No callable contacts in this list.</li>
						) : null}
						{callable.map((c) => {
							const isActive = Number(c.id) === activeId;
							return (
								<li key={String(c.id)}>
									<button
										onClick={() => setActiveId(Number(c.id))}
										className={`w-full rounded-lg border px-2.5 py-2 text-left transition ${
											isActive ? 'border-accent/50 bg-accent/10' : 'border-subtle bg-canvas/40 hover:bg-surface-2'
										}`}
									>
										<p className="truncate text-sm font-medium text-primary">{fullName(c)}</p>
										<p className="truncate text-[11px] text-muted">
											{String((c.company as ApiRecord | null)?.name ?? '')} · {String(c.phone ?? '')}
										</p>
									</button>
								</li>
							);
						})}
					</ul>
				</aside>

				<section className="rounded-2xl border border-subtle bg-surface-2 p-4 shadow-panel">
					{active ? (
						<>
							<div className="flex items-start justify-between">
								<div>
									<p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-muted">On deck</p>
									<h2 className="mt-1 text-lg font-semibold text-primary">{fullName(active)}</h2>
									<p className="text-xs text-muted">
										{String((active.company as ApiRecord | null)?.name ?? 'No company')}
										{active.title ? ` · ${String(active.title)}` : ''}
									</p>
									<p className="mt-1 text-sm text-accent">{String(active.phone ?? '')}</p>
								</div>
								<div className="flex gap-2">
									<button
										onClick={handleDial}
										disabled={!pbxEnabled || activeCall != null}
										className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-500 px-3 py-2 text-sm font-semibold text-accent-contrast hover:bg-emerald-400 disabled:opacity-40"
									>
										<Phone className="h-4 w-4" /> Dial
									</button>
									<button
										onClick={advance}
										className="inline-flex items-center gap-1.5 rounded-xl border border-subtle bg-surface-2 px-3 py-2 text-sm text-secondary hover:bg-surface-hover"
									>
										Skip <ChevronRight className="h-4 w-4" />
									</button>
								</div>
							</div>

							<div className="mt-4">
								<p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-muted">Notes (visible on the contact)</p>
								<textarea
									value={notes}
									onChange={(e) => setNotes(e.target.value)}
									placeholder="What happened on the call?"
									className="mt-1 min-h-20 w-full rounded-xl border border-subtle bg-canvas px-3 py-2 text-sm text-primary"
								/>
							</div>

							<div className="mt-3">
								<p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-muted">Optional follow-up</p>
								<input
									type="datetime-local"
									value={followUpDate}
									onChange={(e) => setFollowUpDate(e.target.value)}
									className="mt-1 w-full rounded-xl border border-subtle bg-canvas px-3 py-2 text-sm text-primary"
								/>
							</div>

							<div className="mt-4">
								<p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-muted">Disposition (one click logs + advances)</p>
								<div className="mt-1.5 grid gap-1.5 sm:grid-cols-2">
									{DISPOSITIONS.map((d) => (
										<button
											key={d.value}
											onClick={() => logMutation.mutate(d)}
											disabled={logMutation.isPending}
											className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm transition ${d.tone} hover:brightness-125`}
										>
											<d.icon className="h-4 w-4" />
											{d.label}
										</button>
									))}
								</div>
							</div>
						</>
					) : (
						<div className="flex h-full items-center justify-center text-sm text-muted">Queue is empty. Pick another status filter.</div>
					)}
				</section>
			</div>
		</div>
	);
}
